// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import IconCollapsableMinus from "@conklin/XDL/Icons/IconCollapsableMinus";
import IconCollapsablePlus from "@conklin/XDL/Icons/IconCollapsablePlus";
import { xBaseTheme } from "@conklin/XDL/types";
import { color, hideElementOnPrint, xScreen } from "@conklin/XDL/Utilities";
import { AccordionSectionPanel } from "@insite/mobius/AccordionSection";
import { AccordionSectionHeaderProps } from "@insite/mobius/AccordionSection/AccordionSectionHeader";
import AccordionSectionPresentationPropsDefault from "@insite/mobius/AccordionSection/presentationProps";
import { ComponentThemeProps } from "@insite/mobius/globals/baseTheme";
import { IconWrapper } from "@insite/mobius/Icon";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { css } from "styled-components";

export default {
    defaultProps: {
        mergeCss: true,
    },
    sectionDefaultProps: {
        ...AccordionSectionPresentationPropsDefault,
        mergeCss: true,
        titleTypographyProps: {
            variant: "h3",
            forwardAs: "span",
            color: "common.ui1",
            css: css`
                margin-bottom: 0;
            `,
        },
        toggleIconProps: {
            color: "common.ui1",
            src: IconCollapsableMinus,
            size: 24,
        },
        expandIconProps: {
            src: IconCollapsablePlus,
        },
        toggleTransition: {
            positionerCss: css`
                display: flex;
                align-items: center;
                justify-content: center;
                border-left: 1px solid ${color("common.border")};
                position: absolute;
                right: 0;
                top: 0;
                max-height: 48px;
                max-width: 48px;
                height: 48px;
                padding-left: 0;
                width: 48px;
                > span:nth-of-type(2) {
                    display: none;
                }
                ${hideElementOnPrint}
            `,
            transitionCss: css`
                position: relative;
                bottom: auto;
                right: auto;
                height: 24px;
                opacity: 0.65;

                &:hover {
                    opacity: 1;
                }
            `,
        },
        headerProps: {
            css: css`
                button {
                    padding: 12px;
                    border: 1px solid ${color("common.border")};
                    background: ${color("primary")};
                    border-radius: 4px 4px 0 0;
                    position: relative;

                    &[aria-expanded="false"] {
                        border-radius: 4px;
                    }
                    @media print {
                        display: block !important;
                    }
                }

                &&& button,
                &&& ${/* sc-selector */ AccordionSectionPanel} + & button {
                    border-top: 1px solid ${color("common.border")};
                    &:focus {
                        background: ${color("primary")};
                        border: 1px solid ${color("common.border")};
                        outline-width: 0;
                        padding: 12px;
                    }
                }

                ${({ expanded }: AccordionSectionHeaderProps) =>
                    !expanded &&
                    css`
                        margin-bottom: 32px;
                        &&& button,
                        &&& button:focus,
                        ${AccordionSectionPanel} + & button:focus {
                            border-bottom-color: ${color("common.border")};
                        }
                        @media print {
                            margin-bottom: 0;
                        }
                    `}
            `,
        },
        panelProps: {
            css: css`
                padding: 12px;
                border-radius: 0 0 4px 4px;
                margin-bottom: 20px;
            `,
        },
    },
} as xBaseTheme["accordion"];

export const SecondaryAccordionProps: ComponentThemeProps["accordion"]["sectionDefaultProps"] = {
    titleTypographyProps: {
        variant: "h6",
        forwardAs: "span",
        color: "common.ui1",
        css: css`
            margin-bottom: 0;
        `,
    },
    toggleIconProps: {
        color: "common.ui1",
        src: IconCollapsableMinus,
        size: 20,
    },
    toggleTransition: {
        positionerCss: css`
            display: flex;
            align-items: center;
            justify-content: center;
            border-left: 1px solid ${color("common.border")};
            position: absolute;
            right: 0;
            top: 0;
            max-height: 34px;
            max-width: 34px;
            height: 34px;
            padding-left: 0;
            width: 34px;
            > span:nth-of-type(2) {
                display: none;
            }
        `,
        transitionCss: css`
            position: relative;
            bottom: auto;
            right: auto;
            height: 20;
        `,
    },
    headerProps: {
        css: css`
            button {
                border-radius: 0;
                background-color: ${color("common.ui11")};
                padding: 8px;

                &[aria-expanded="false"] {
                    border-radius: 0;
                    background-color: ${color("common.ui1")};
                }
            }

            &&& button,
            &&& ${/* sc-selector */ AccordionSectionPanel} + & button {
                &:focus {
                    background: ${color("common.ui11")};
                    padding: 8px;
                }
            }

            ${({ expanded }: AccordionSectionHeaderProps) =>
                !expanded &&
                css`
                    margin-bottom: 8px;
                    &&& button,
                    &&& button:focus,
                    ${AccordionSectionPanel} + & button:focus {
                        border-bottom-color: ${color("common.border")};
                    }

                    ${TypographyStyle} {
                        color: ${color("common.ui7")};
                    }

                    ${IconWrapper} {
                        color: ${color("common.ui5")};
                    }

                    &&& button,
                    &&& ${/* sc-selector */ AccordionSectionPanel} + & button {
                        &:focus {
                            background: ${color("common.ui1")};
                        }
                    }
                `}
        `,
    },
    panelProps: {
        css: css`
            padding: 12px;
            border-radius: 0;
            margin-bottom: 8px;
        `,
    },
};
